import React from 'react';
import { Group, Line, Rect, Circle, Ellipse, Text } from 'react-konva';
import { useCanvasStore } from '../../store/canvasStore';
import { useEditorStore } from '../../store/editorStore';
import { THEMES } from '../../floorplanTheme';
import { getSymbolPrimitives } from '../../symbolGeometry';
import type { CanvasShape } from '../../types';

interface SymbolShapeProps {
  shape: CanvasShape;
  onCanvasChange?: () => void;
}

const MIN = 12;

const SymbolShape: React.FC<SymbolShapeProps> = ({ shape, onCanvasChange }) => {
  const { updateShape, removeShape, pushHistory } = useCanvasStore();
  const { selectedIds, setSelectedIds, activeTool, canvasTheme } = useEditorStore();
  const theme = THEMES[canvasTheme];
  const isSelected = selectedIds.includes(shape.id);

  const w = Math.max(MIN, shape.width ?? 40);
  const h = Math.max(MIN, shape.height ?? 40);
  const stroke = isSelected ? theme.wall.selectedStroke : theme.wall.stroke;
  const strokeWidth = 1.75;
  const flipX = !!shape.flipX;
  const flipY = !!shape.flipY;
  const prims = getSymbolPrimitives(shape.symbolId ?? 'door', w, h, {
    stairDirection: shape.stairDirection,
  });

  const handleSelect = () => {
    if (activeTool === 'select') setSelectedIds([shape.id]);
    if (activeTool === 'eraser') {
      removeShape(shape.id);
      pushHistory();
      onCanvasChange?.();
    }
  };

  const handleDragEnd = (e: any) => {
    updateShape(shape.id, { x: e.target.x(), y: e.target.y() });
    pushHistory();
    onCanvasChange?.();
  };

  const handleTransformEnd = (e: any) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    updateShape(shape.id, {
      x: node.x(),
      y: node.y(),
      width: Math.max(MIN, w * scaleX),
      height: Math.max(MIN, h * scaleY),
      rotation: node.rotation(),
    });
    pushHistory();
    onCanvasChange?.();
  };

  return (
    <Group
      id={shape.id}
      name="transformable"
      x={shape.x}
      y={shape.y}
      rotation={shape.rotation ?? 0}
      draggable={activeTool === 'select'}
      onClick={handleSelect}
      onTap={handleSelect}
      onMouseDown={(e) => {
        e.cancelBubble = true;
      }}
      onDragEnd={handleDragEnd}
      onTransformEnd={handleTransformEnd}
    >
      <Group x={flipX ? w : 0} y={flipY ? h : 0} scaleX={flipX ? -1 : 1} scaleY={flipY ? -1 : 1}>
      {/* Transparent hit area so the whole bounding box is grabbable, including the door swing. */}
      <Rect x={-6} y={-14} width={w + 12} height={h + 22} fill="rgba(0,0,0,0.001)" />
      {prims.map((p, i) => {
        if (p.k === 'hatch') {
          return (
            <Rect
              key={i}
              x={p.x}
              y={p.y}
              width={p.w}
              height={p.h}
              fill={theme.canvas.background}
              listening={false}
            />
          );
        }
        if (p.k === 'line') {
          return <Line key={i} points={p.pts} stroke={stroke} strokeWidth={strokeWidth} lineCap="round" lineJoin="round" listening={false} />;
        }
        if (p.k === 'rect') {
          return (
            <Rect
              key={i}
              x={p.x}
              y={p.y}
              width={p.w}
              height={p.h}
              cornerRadius={p.r ?? 0}
              stroke={stroke}
              strokeWidth={strokeWidth}
              listening={false}
            />
          );
        }
        if (p.k === 'circle') {
          return <Circle key={i} x={p.x} y={p.y} radius={p.r} stroke={stroke} strokeWidth={strokeWidth} listening={false} />;
        }
        if (p.k === 'text') {
          const tw = p.text.length * p.size * 0.7 + 4;
          return (
            <Text
              key={i}
              x={p.x - tw / 2}
              y={p.y - p.size / 2}
              width={tw}
              align="center"
              text={p.text}
              fontSize={p.size}
              fontFamily="system-ui, -apple-system, sans-serif"
              fill={stroke}
              listening={false}
            />
          );
        }
        return <Ellipse key={i} x={p.x} y={p.y} radiusX={p.rx} radiusY={p.ry} stroke={stroke} strokeWidth={strokeWidth} listening={false} />;
      })}
      </Group>
    </Group>
  );
};

export default SymbolShape;
